import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, 
  Sun, 
  ShieldCheck, 
  ThermometerSnowflake,
  MapPin,
  Mail,
  Facebook,
  CheckCircle2,
  Zap,
  DollarSign,
  Clock,
  Award,
  Menu,
  X
} from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to proxy external images through backend (fixes iOS Safari CORS issues)
const proxyImage = (url) => `${API}/images/proxy?url=${encodeURIComponent(url)}`;

// All customer asset image URLs
const SITE_IMAGES = {
  // Logo
  logo: "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png",
  // Florida-Proof comparison (US vs THEM)
  floridaProof: "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/doyijepi_55e9ce75-4715-4a5d-a317-f7c1848400d0.jpeg",
  // Cool Roof Solution flyer
  coolRoofFlyer: "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/7t4sabbw_2E9200AC-FFD8-44E7-BA2F-4041D47A6E74.png",
  // Service areas image
  serviceAreas: "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/wdpxrhtp_35A5B7C9-E791-468D-BB13-60EF0C8B0415.png",
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    roof_type: '',
    square_footage: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await axios.post(`${API}/quote-requests`, formData);
      setSubmitMessage('Thank you! We will contact you within 24 hours.');
      setFormData({
        name: '',
        phone: '',
        email: '',
        roof_type: '',
        square_footage: '',
        city: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage('Something went wrong. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Primary LocalBusiness Schema with enhanced E-E-A-T signals
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["RoofingContractor", "LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": "https://savagerejuvenationcoatings.com/#business",
    "name": "Savage Rejuvenation Coatings",
    "alternateName": "Savage Rejuvenation",
    "legalName": "Savage Rejuvenation Coatings LLC",
    "image": [
      "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png",
      "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/7t4sabbw_2E9200AC-FFD8-44E7-BA2F-4041D47A6E74.png"
    ],
    "logo": "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png",
    "description": "Central Florida's premier roof coating contractors specializing in cool roof coatings that reduce AC bills by 20-30%, extend roof life 10+ years, and provide hurricane-tough waterproof protection. Family-owned, licensed, and insured serving Orlando, Tampa, Lakeland, Kissimmee, and all of Central Florida.",
    "url": "https://savagerejuvenationcoatings.com",
    "telephone": "+1-321-804-4413",
    "email": "contact@savagerejuvenationcoatings.com",
    "foundingDate": "2020",
    "founder": {
      "@type": "Person",
      "name": "Jason",
      "jobTitle": "Owner & Lead Technician"
    },
    "employee": [
      {
        "@type": "Person",
        "name": "Jason",
        "jobTitle": "Owner & Lead Technician"
      },
      {
        "@type": "Person",
        "name": "Lainie",
        "jobTitle": "Customer Relations Manager"
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Central Florida",
      "addressLocality": "Orlando",
      "addressRegion": "FL",
      "postalCode": "32801",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 28.5383,
      "longitude": -81.3792
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Orlando",
        "sameAs": "https://en.wikipedia.org/wiki/Orlando,_Florida"
      },
      {
        "@type": "City",
        "name": "Tampa",
        "sameAs": "https://en.wikipedia.org/wiki/Tampa,_Florida"
      },
      {
        "@type": "City",
        "name": "Lakeland",
        "sameAs": "https://en.wikipedia.org/wiki/Lakeland,_Florida"
      },
      {
        "@type": "City",
        "name": "Kissimmee",
        "sameAs": "https://en.wikipedia.org/wiki/Kissimmee,_Florida"
      },
      {
        "@type": "City",
        "name": "Winter Park",
        "sameAs": "https://en.wikipedia.org/wiki/Winter_Park,_Florida"
      },
      {
        "@type": "City",
        "name": "Clearwater",
        "sameAs": "https://en.wikipedia.org/wiki/Clearwater,_Florida"
      },
      {
        "@type": "City",
        "name": "St. Petersburg",
        "sameAs": "https://en.wikipedia.org/wiki/St._Petersburg,_Florida"
      },
      {
        "@type": "City",
        "name": "Sanford",
        "sameAs": "https://en.wikipedia.org/wiki/Sanford,_Florida"
      },
      {
        "@type": "City",
        "name": "Daytona Beach",
        "sameAs": "https://en.wikipedia.org/wiki/Daytona_Beach,_Florida"
      },
      {
        "@type": "City",
        "name": "Melbourne",
        "sameAs": "https://en.wikipedia.org/wiki/Melbourne,_Florida"
      },
      {
        "@type": "City",
        "name": "Ocala",
        "sameAs": "https://en.wikipedia.org/wiki/Ocala,_Florida"
      },
      {
        "@type": "City",
        "name": "Deltona",
        "sameAs": "https://en.wikipedia.org/wiki/Deltona,_Florida"
      },
      {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 28.5383,
          "longitude": -81.3792
        },
        "geoRadius": "100 mi"
      }
    ],
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": "Cash, Credit Card, Check, Financing Available",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/share/18YBw8kmoS/?mibextid=wwXIfr"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Michael Rodriguez"
        },
        "reviewBody": "Jason and his team did an incredible job on our roof coating! Our AC bill dropped by almost 30% in the first month. The house stays so much cooler now. Highly recommend Savage Rejuvenation!",
        "datePublished": "2026-03-01"
      },
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Sarah Chen"
        },
        "reviewBody": "Professional, punctual, and the results are amazing. Our metal roof looks brand new and the heat reduction is noticeable. Worth every penny!",
        "datePublished": "2026-02-15"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Professional Roof Coating Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cool Roof Coating for Asphalt Shingles",
            "description": "Reflective nano-ceramic coating that reduces heat absorption by 85%, cuts AC bills 20-30%, and extends asphalt shingle roof life by 10+ years",
            "areaServed": "Central Florida",
            "provider": {
              "@id": "https://savagerejuvenationcoatings.com/#business"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Metal Roof Restoration & Coating",
            "description": "Professional rust-preventive coating for metal roofs with UV protection and heat reduction technology",
            "areaServed": "Central Florida",
            "provider": {
              "@id": "https://savagerejuvenationcoatings.com/#business"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Tile Roof Sealing & Waterproofing",
            "description": "Waterproof coating for tile roofs with UV protection and hurricane-resistant formula",
            "areaServed": "Central Florida",
            "provider": {
              "@id": "https://savagerejuvenationcoatings.com/#business"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Flat Roof & TPO Coating",
            "description": "Commercial-grade coating for flat roofs and TPO membranes with superior waterproofing",
            "areaServed": "Central Florida",
            "provider": {
              "@id": "https://savagerejuvenationcoatings.com/#business"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Hurricane Protection Coating",
            "description": "Heavy-duty waterproof coating designed to withstand Florida hurricanes and severe weather",
            "areaServed": "Central Florida",
            "provider": {
              "@id": "https://savagerejuvenationcoatings.com/#business"
            }
          }
        }
      ]
    },
    "knowsAbout": [
      "Roof Coating",
      "Cool Roof Technology",
      "Energy Efficient Roofing",
      "Hurricane Protection",
      "Asphalt Shingle Restoration",
      "Metal Roof Coating",
      "Tile Roof Sealing",
      "Waterproof Roofing",
      "UV Resistant Coatings",
      "Florida Roofing"
    ],
    "slogan": "Transform Your Roof in ONE DAY!",
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "license",
        "name": "Florida Roofing Contractor License"
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "certification",
        "name": "Certified Cool Roof Applicator"
      }
    ]
  };

  // Organization Schema for brand recognition
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://savagerejuvenationcoatings.com/#organization",
    "name": "Savage Rejuvenation Coatings",
    "url": "https://savagerejuvenationcoatings.com",
    "logo": "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-321-804-4413",
      "contactType": "customer service",
      "areaServed": "US-FL",
      "availableLanguage": ["English", "Spanish"]
    },
    "sameAs": [
      "https://www.facebook.com/share/18YBw8kmoS/?mibextid=wwXIfr"
    ]
  };

  // WebSite Schema for sitelinks search box
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Savage Rejuvenation Coatings",
    "url": "https://savagerejuvenationcoatings.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://savagerejuvenationcoatings.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // BreadcrumbList Schema for navigation
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Savage Rejuvenation Coatings | #1 Roof Coating Orlando Tampa FL | Reduce AC Bills 30%</title>
        <meta name="description" content="Central Florida's premier roof coating contractors. Cool roof coatings for Orlando, Tampa, Lakeland, Kissimmee & beyond. Reduce AC bills 20-30%, extend roof life 10+ years. Hurricane-tough, fully warranted. Licensed & insured. Call (321) 804-4413 for FREE inspection." />
        <meta name="keywords" content="roof coating Orlando, cool roof Tampa, roof rejuvenation Central Florida, asphalt shingle coating Orlando, metal roof coating Tampa, reduce AC bill Florida, hurricane proof roof coating, roof sealing Orlando FL, energy efficient roof coating, waterproof roof coating Florida, roof coating near me, best roofer Orlando, Tampa roof contractor, Lakeland roof coating, Kissimmee roofing" />
        
        <meta property="og:title" content="Savage Rejuvenation Coatings | Transform Your Roof in ONE DAY" />
        <meta property="og:description" content="Reduce AC bills 20-30% with cool roof coatings. Licensed, insured, family-owned. Serving Orlando, Tampa & Central Florida. FREE inspections!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://savagerejuvenationcoatings.com" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png" />
        <meta property="og:image:alt" content="Savage Rejuvenation Coatings Logo - Roof Coating Experts in Central Florida" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Savage Rejuvenation Coatings" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Savage Rejuvenation Coatings | #1 Roof Coating Central Florida" />
        <meta name="twitter:description" content="Cool roof coatings that reduce AC bills 20-30%. Orlando, Tampa & Central Florida. FREE inspection!" />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando, Florida" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />
        
        <meta name="author" content="Savage Rejuvenation Coatings" />
        <meta name="publisher" content="Savage Rejuvenation Coatings" />
        <meta name="copyright" content="Savage Rejuvenation Coatings" />
        
        <meta name="format-detection" content="telephone=yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Savage Rejuvenation" />
        
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary shadow-[0_4px_0_0_rgba(0,0,0,0.1)]" data-testid="header">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3">
            <div className="flex items-center justify-between">
              <nav className="hidden lg:flex items-center gap-6">
                <a href="/reviews" className="text-white hover:text-primary transition-colors font-manrope font-semibold">Reviews</a>
                <a href="/qa" className="text-white hover:text-primary transition-colors font-manrope font-semibold">Q&A</a>
                <a href="/blog" className="text-white hover:text-primary transition-colors font-manrope font-semibold">Blog</a>
              </nav>
              <div className="hidden lg:flex items-center gap-6">
                <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold" data-testid="header-phone-business">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </div>
              <div className="lg:hidden flex items-center gap-3">
                <a href="tel:3218044413" data-testid="mobile-call-button">
                  <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                    <Phone className="h-5 w-5" />
                  </Button>
                </a>
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-white p-2"
                  data-testid="mobile-menu-button"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t border-gray-600 pt-4 flex flex-col gap-4">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold text-lg">Home</a>
                <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold text-lg">Reviews</a>
                <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold text-lg">Q&A</a>
                <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold text-lg">Blog</a>
                <a href="tel:3218044413" className="text-primary font-manrope font-semibold text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </nav>
            )}
          </div>
        </header>

        <section 
          className="relative min-h-[700px] md:min-h-[800px] flex flex-col items-center justify-center bg-cover bg-center asphalt-texture" 
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1674485169641-bcb2bf6f1df9?crop=entropy&cs=srgb&fm=jpg&q=85')`
          }}
          data-testid="hero-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-white text-center">
            <div className="mb-6">
              <img 
                src={SITE_IMAGES.logo}
                alt="Savage Rejuvenation Coatings"
                className="h-72 md:h-80 lg:h-96 w-auto mx-auto mb-4"
                crossOrigin="anonymous"
                onError={(e) => { if (!e.target.src.includes('/api/images/proxy')) e.target.src = proxyImage(SITE_IMAGES.logo); }}
              />
              <p className="font-barlow text-lg md:text-xl uppercase text-primary font-bold tracking-wide">
                Central Florida's Premier Roof Coating Experts
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <h2 className="font-barlow text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-6 leading-tight" data-testid="hero-title">
                Transform Your Roof in <span className="text-primary">ONE DAY!</span>
              </h2>
              <p className="text-2xl md:text-3xl font-barlow uppercase tracking-tight mb-4 text-gray-200" data-testid="hero-subtitle">
                Central Florida's Toughest Roof Protection
              </p>
              <ul className="space-y-3 mb-8 text-lg font-manrope text-left max-w-2xl mx-auto">
                <li className="flex items-start gap-3" data-testid="benefit-heat-reflection">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <span><strong className="text-primary">Reflects 85% Solar Heat</strong> - Cut AC Bills 20-30%</span>
                </li>
                <li className="flex items-start gap-3" data-testid="benefit-roof-life">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <span><strong className="text-primary">Extends Roof Life</strong> - Up to 10+ Years</span>
                </li>
                <li className="flex items-start gap-3" data-testid="benefit-hurricane-proof">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <span><strong className="text-primary">Hurricane-Tough</strong> - Waterproof & UV-Resistant</span>
                </li>
                <li className="flex items-start gap-3" data-testid="benefit-warranty">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <span><strong className="text-primary">Fully Warrantied</strong> - Results You Can Trust</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:3218044413" data-testid="hero-call-button">
                  <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000] hover:-translate-y-1 transition-transform rounded-sm text-lg px-8 py-6 font-barlow uppercase font-bold w-full sm:w-auto">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now for FREE Inspection
                  </Button>
                </a>
                <a href="#quote" data-testid="hero-quote-button">
                  <Button className="bg-white hover:bg-gray-100 text-black border-2 border-black shadow-[4px_4px_0px_0px_#000] hover:-translate-y-1 transition-transform rounded-sm text-lg px-8 py-6 font-barlow uppercase font-bold w-full sm:w-auto">
                    Get FREE Quote
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-6 bg-primary border-y-4 border-black" data-testid="trust-band">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center" data-testid="trust-licensed">
                <ShieldCheck className="h-10 w-10 text-white mb-2" />
                <p className="text-white font-barlow uppercase font-bold text-sm md:text-base">Licensed & Insured</p>
              </div>
              <div className="flex flex-col items-center" data-testid="trust-family">
                <Award className="h-10 w-10 text-white mb-2" />
                <p className="text-white font-barlow uppercase font-bold text-sm md:text-base">Family Owned</p>
              </div>
              <div className="flex flex-col items-center" data-testid="trust-fast">
                <Clock className="h-10 w-10 text-white mb-2" />
                <p className="text-white font-barlow uppercase font-bold text-sm md:text-base">Fast Cure</p>
              </div>
              <div className="flex flex-col items-center" data-testid="trust-warranty">
                <CheckCircle2 className="h-10 w-10 text-white mb-2" />
                <p className="text-white font-barlow uppercase font-bold text-sm md:text-base">Fully Warrantied</p>
              </div>
            </div>
          </div>
        </section>



        <section className="py-20 md:py-32 bg-white" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4" data-testid="services-title">
                Professional Coating for All Roof Types
              </h2>
              <p className="text-xl font-manrope text-gray-700">
                Asphalt, Metal, Tile - We've Got Central Florida Covered
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <a href="/roofs/asphalt-shingles" className="relative h-96 overflow-hidden border-2 border-black shadow-[4px_4px_0px_0px_#000] group cursor-pointer" data-testid="service-card-asphalt">
                <img 
                  src="https://images.unsplash.com/photo-1680035579849-3185807fb1ea?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Asphalt Shingle Roof Coating Orlando Florida"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="font-barlow text-2xl font-bold uppercase text-white mb-2">Asphalt Shingles</h3>
                    <p className="font-manrope text-gray-200">Seal cracks, restore color, and add years of life to asphalt roofs.</p>
                    <p className="font-manrope text-primary mt-2 font-semibold">Learn More →</p>
                  </div>
                </div>
              </a>
              <a href="/roofs/metal" className="relative h-96 overflow-hidden border-2 border-black shadow-[4px_4px_0px_0px_#000] group cursor-pointer" data-testid="service-card-metal">
                <img 
                  src="https://images.unsplash.com/photo-1653889676142-9832054b9911?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Metal Roof Coating Tampa Florida"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="font-barlow text-2xl font-bold uppercase text-white mb-2">Metal Roofs</h3>
                    <p className="font-manrope text-gray-200">Prevent rust and reduce heat on commercial and residential metal roofs.</p>
                    <p className="font-manrope text-primary mt-2 font-semibold">Learn More →</p>
                  </div>
                </div>
              </a>
              <a href="/roofs/tile" className="relative h-96 overflow-hidden border-2 border-black shadow-[4px_4px_0px_0px_#000] group cursor-pointer" data-testid="service-card-tile">
                <img 
                  src="https://images.unsplash.com/photo-1645892399945-bad477acd3e3?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Tile Roof Coating Central Florida"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="font-barlow text-2xl font-bold uppercase text-white mb-2">Tile Roofs</h3>
                    <p className="font-manrope text-gray-200">Waterproof and protect tile roofs with UV-resistant coating.</p>
                    <p className="font-manrope text-primary mt-2 font-semibold">Learn More →</p>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-32 bg-gray-50" data-testid="problem-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Real Results Header */}
            <div className="text-center mb-12">
              <span className="inline-block bg-primary/10 text-primary font-barlow uppercase font-bold text-sm px-4 py-2 mb-4 border-2 border-primary">
                Real Results
              </span>
              <h2 className="font-barlow text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-6">
                See the <span className="text-primary">Difference</span>
              </h2>
              <p className="font-manrope text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-12">
                These photos showcase actual project results from our professional roof rejuvenation process. 
                Our advanced coating technology restores appearance, improves protection against Florida&apos;s 
                harsh weather, and helps extend the useful life of qualifying roofs.
              </p>
              <img 
                src={SITE_IMAGES.floridaProof}
                alt="Florida Proof Your Roof - Before and After Comparison showing US vs THEM results"
                className="w-full max-w-5xl mx-auto border-4 border-primary shadow-[8px_8px_0px_0px_#FF8C00] mb-12"
                crossOrigin="anonymous"
                onError={(e) => { if (!e.target.src.includes('/api/images/proxy')) e.target.src = proxyImage(SITE_IMAGES.floridaProof); }}
              />
            </div>
            
            <div className="text-center mb-12">
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4" data-testid="problem-title">
                Florida Heat is <span className="text-red-600">DESTROYING</span> Your Roof
              </h2>
              <p className="text-xl font-manrope text-gray-700 max-w-3xl mx-auto">
                Central Florida&apos;s intense UV rays, brutal heat, and hurricane-force weather crack shingles, 
                fade color, and turn your attic into an oven—driving up AC costs and shortening roof life.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000] p-6" data-testid="problem-card-1">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Sun className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-2">Extreme Heat Absorption</h3>
                <p className="font-manrope text-gray-600">Dark roofs absorb up to 90% of solar heat, making your AC work overtime and costing you hundreds monthly.</p>
              </div>
              <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000] p-6" data-testid="problem-card-2">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Zap className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-2">UV & Weather Damage</h3>
                <p className="font-manrope text-gray-600">Florida's UV rays crack and fade roofs. Add hurricanes and rain, and you're facing leaks and expensive repairs.</p>
              </div>
              <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000] p-6" data-testid="problem-card-3">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <DollarSign className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-2">Costly Replacement</h3>
                <p className="font-manrope text-gray-600">The average roof replacement in Florida costs $15,000-$30,000. Why replace when you can protect and extend?</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-32 bg-gradient-to-b from-accent-light to-white" data-testid="solution-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4" data-testid="solution-title">
                The <span className="text-accent">Cool Roof</span> Solution
              </h2>
              <p className="text-xl font-manrope text-gray-700 max-w-3xl mx-auto">
                Our premium nano-ceramic roof coating creates a reflective, waterproof shield that transforms 
                your roof into a heat-rejecting, energy-saving, hurricane-tough barrier.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src={SITE_IMAGES.coolRoofFlyer}
                  alt="Savage Rejuvenation Coatings Before and After Results - Transform Your Roof in One Day"
                  className="w-full border-4 border-primary shadow-[8px_8px_0px_0px_#FF8C00] hover:shadow-[12px_12px_0px_0px_#FF8C00] transition-all duration-300"
                  data-testid="before-after-image"
                  crossOrigin="anonymous"
                  onError={(e) => { if (!e.target.src.includes('/api/images/proxy')) e.target.src = proxyImage(SITE_IMAGES.coolRoofFlyer); }}
                />
              </div>
              <div className="space-y-6">
                <div className="flex gap-4" data-testid="feature-heat">
                  <div className="bg-accent w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <ThermometerSnowflake className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">85% Heat Reflection</h3>
                    <p className="font-manrope text-gray-700">Advanced reflective technology bounces solar heat away, keeping your home cool and slashing AC costs by 20-30%.</p>
                  </div>
                </div>
                <div className="flex gap-4" data-testid="feature-waterproof">
                  <div className="bg-accent w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Waterproof Protection</h3>
                    <p className="font-manrope text-gray-700">Seals cracks and creates an impenetrable barrier against Florida rain and hurricanes. No more leaks.</p>
                  </div>
                </div>
                <div className="flex gap-4" data-testid="feature-life">
                  <div className="bg-accent w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">10+ Year Extension</h3>
                    <p className="font-manrope text-gray-700">Extends your roof's life by a decade or more, saving you thousands on replacement costs.</p>
                  </div>
                </div>
                <div className="flex gap-4" data-testid="feature-eco">
                  <div className="bg-accent w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Eco-Friendly Formula</h3>
                    <p className="font-manrope text-gray-700">Safe, low-VOC coating. No tar-off needed for Florida roofs—we coat right over your existing surface.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-black text-white border-y-4 border-primary" data-testid="promo-section">
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
            <div className="bg-primary text-white inline-block px-6 py-2 border-2 border-white shadow-[4px_4px_0px_0px_#fff] mb-6 rotate-[-2deg]">
              <p className="font-barlow text-lg uppercase font-bold tracking-wide">Limited Time Offer</p>
            </div>
            <h2 className="font-barlow text-5xl md:text-6xl font-bold uppercase tracking-tight mb-4" data-testid="promo-title">
              $500 OFF Full Roof Rejuvenation
            </h2>
            <p className="text-xl font-manrope mb-2">For Roofs Under 2,000 sq ft</p>
            <p className="text-lg font-manrope text-gray-300 mb-6">Expires 7/31/26 - Mention This Offer!</p>
            <p className="text-2xl font-barlow uppercase mb-8 text-primary">+ FREE Inspection - No Obligation</p>
            <a href="tel:3218044413" data-testid="promo-call-button">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-white shadow-[4px_4px_0px_0px_#fff] hover:-translate-y-1 transition-transform rounded-sm text-xl px-10 py-7 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-6 w-6" />
                Call Now: (321) 804-4413
              </Button>
            </a>
          </div>
        </section>

        <section className="py-20 md:py-32 bg-gray-50" data-testid="service-areas-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4" data-testid="areas-title">
                Proudly Serving Central Florida
              </h2>
              <p className="text-xl font-manrope text-gray-700">Orlando, Tampa & Beyond</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { name: 'Orlando', url: 'https://www.orlando.gov/' },
                { name: 'Tampa', url: 'https://www.tampa.gov/' },
                { name: 'Lakeland', url: 'https://www.lakelandgov.net/' },
                { name: 'Kissimmee', url: 'https://www.kissimmee.gov/' },
                { name: 'Winter Park', url: 'https://cityofwinterpark.org/' },
                { name: 'Clearwater', url: 'https://www.myclearwater.com/' },
                { name: 'St. Petersburg', url: 'https://www.stpete.org/' },
                { name: 'Sanford', url: 'https://www.sanfordfl.gov/' },
                { name: 'Apopka', url: 'https://www.apopka.net/' },
                { name: 'Oviedo', url: 'https://www.cityofoviedo.net/' },
                { name: 'Plant City', url: 'https://www.plantcitygov.com/' },
                { name: 'Brandon', url: 'https://www.hillsboroughcounty.org/' },
                { name: 'Winter Haven', url: 'https://www.mywinterhaven.com/' },
                { name: 'Clermont', url: 'https://www.clermont.gov/' },
                { name: 'Davenport', url: 'https://www.davenportcityfl.org/' },
                { name: 'Haines City', url: 'https://www.hainescity.com/' }
              ].map((city) => (
                <a
                  key={city.name}
                  href={city.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border-2 border-black shadow-[2px_2px_0px_0px_#000] p-4 text-center hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_#000] transition-all"
                  data-testid={`service-city-${city.name.toLowerCase().replace(' ', '-')}`}
                >
                  <MapPin className="h-5 w-5 text-primary mx-auto mb-2" />
                  <p className="font-barlow uppercase font-bold text-sm">{city.name}</p>
                </a>
              ))}
            </div>
            <p className="text-center mt-8 font-manrope text-gray-600">
              Don't see your city? <a href="tel:3218044413" className="text-primary font-bold hover:underline">Call us</a> - we likely serve your area!
            </p>
          </div>
        </section>

        <section id="quote" className="py-20 md:py-32 bg-white" data-testid="quote-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4" data-testid="quote-title">
                Get Your FREE Quote Today
              </h2>
              <p className="text-xl font-manrope text-gray-700">
                Fill out the form below or call us directly for immediate assistance
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 border-2 border-black shadow-[6px_6px_0px_0px_#000] p-8" data-testid="quote-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block font-barlow uppercase font-bold mb-2">Your Name *</label>
                  <Input
                    id="name"
                    data-testid="quote-input-name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="border-2 border-black rounded-sm focus:ring-primary"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block font-barlow uppercase font-bold mb-2">Phone Number *</label>
                  <Input
                    id="phone"
                    data-testid="quote-input-phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="border-2 border-black rounded-sm focus:ring-primary"
                    placeholder="(407) 555-0123"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block font-barlow uppercase font-bold mb-2">Email</label>
                <Input
                  id="email"
                  data-testid="quote-input-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="border-2 border-black rounded-sm focus:ring-primary"
                  placeholder="john@example.com"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="roof_type" className="block font-barlow uppercase font-bold mb-2">Roof Type *</label>
                  <Select 
                    required
                    value={formData.roof_type}
                    onValueChange={(value) => setFormData({...formData, roof_type: value})}
                  >
                    <SelectTrigger className="border-2 border-black rounded-sm" data-testid="quote-select-roof-type">
                      <SelectValue placeholder="Select roof type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asphalt">Asphalt Shingles</SelectItem>
                      <SelectItem value="metal">Metal Roof</SelectItem>
                      <SelectItem value="tile">Tile Roof</SelectItem>
                      <SelectItem value="other">Other / Not Sure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="square_footage" className="block font-barlow uppercase font-bold mb-2">Approx. Square Footage</label>
                  <Input
                    id="square_footage"
                    data-testid="quote-input-sqft"
                    value={formData.square_footage}
                    onChange={(e) => setFormData({...formData, square_footage: e.target.value})}
                    className="border-2 border-black rounded-sm focus:ring-primary"
                    placeholder="2000"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="city" className="block font-barlow uppercase font-bold mb-2">Your City *</label>
                <Input
                  id="city"
                  data-testid="quote-input-city"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="border-2 border-black rounded-sm focus:ring-primary"
                  placeholder="Orlando"
                />
              </div>
              <div>
                <label htmlFor="message" className="block font-barlow uppercase font-bold mb-2">Additional Details</label>
                <Textarea
                  id="message"
                  data-testid="quote-input-message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="border-2 border-black rounded-sm focus:ring-primary min-h-[100px]"
                  placeholder="Tell us about your roof condition or any specific concerns..."
                />
              </div>
              {submitMessage && (
                <div className={`p-4 border-2 border-black ${submitMessage.includes('Thank you') ? 'bg-green-100' : 'bg-red-100'}`} data-testid="quote-submit-message">
                  <p className="font-manrope font-semibold">{submitMessage}</p>
                </div>
              )}
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000] hover:-translate-y-1 transition-transform rounded-sm text-xl py-7 font-barlow uppercase font-bold"
                data-testid="quote-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Get My FREE Quote'}
              </Button>
            </form>
          </div>
        </section>

        <footer className="bg-secondary text-white py-12 border-t-4 border-primary" data-testid="footer">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-3 gap-12 mb-8">
              <div>
                <img 
                  src={SITE_IMAGES.serviceAreas}
                  alt="Savage Rejuvenation Coatings" 
                  className="h-16 w-auto mb-4"
                  crossOrigin="anonymous"
                  onError={(e) => { if (!e.target.src.includes('/api/images/proxy')) e.target.src = proxyImage(SITE_IMAGES.serviceAreas); }}
                />
                <p className="font-manrope text-gray-300">
                  Central Florida's premier roof coating contractor. Licensed, insured, and family-owned.
                </p>
              </div>
              <div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-4">Contact Us</h3>
                <div className="space-y-3 font-manrope">
                  <a href="tel:3218044413" className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="footer-phone-business">
                    <Phone className="h-5 w-5" />
                    (321) 804-4413
                  </a>
                  <a href="mailto:contact@savagerejuvenationcoatings.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail className="h-5 w-5" />
                    <span>contact@savagerejuvenationcoatings.com</span>
                  </a>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>Central Florida</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-4">Follow Us</h3>
                <a 
                  href="https://www.facebook.com/share/18YBw8kmoS/?mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover px-6 py-3 border-2 border-white shadow-[2px_2px_0px_0px_#fff] hover:-translate-y-1 transition-transform rounded-sm font-barlow uppercase font-bold"
                  data-testid="footer-facebook-link"
                >
                  <Facebook className="h-5 w-5" />
                  Visit Our Facebook
                </a>
                <p className="font-manrope text-gray-300 mt-4 text-sm">
                  @SavageRejuvenationCoatings
                </p>
              </div>
            </div>
            <div className="border-t border-gray-700 pt-8 text-center font-manrope text-gray-400 text-sm">
              <p>&copy; 2026 Savage Rejuvenation Coatings. All rights reserved. Licensed & Insured FL Contractor.</p>
              <p className="mt-2">
                <strong>Contact:</strong> (321) 804-4413 | savagerejuvenationcoatings.com
              </p>
              <p className="mt-2">
                <strong>Service Areas:</strong> Orlando, Tampa, Lakeland, Kissimmee, Winter Park, Clearwater, St. Petersburg, and all of Central Florida
              </p>
            </div>
          </div>
        </footer>

        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-secondary border-t-4 border-primary shadow-lg" data-testid="mobile-cta-bar">
          <div className="grid grid-cols-2 gap-2 p-3">
            <a href="tel:3218044413" className="block" data-testid="mobile-sticky-call-button">
              <Button className="w-full bg-primary hover:bg-primary-hover text-white border-2 border-white rounded-sm py-6 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
            <a href="#quote" className="block" data-testid="mobile-sticky-quote-button">
              <Button className="w-full bg-white hover:bg-gray-100 text-black border-2 border-white rounded-sm py-6 font-barlow uppercase font-bold">
                Get Quote
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
